// React
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

// External imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faMap,
  faMemoCircleInfo
} from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeft,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import Container from 'react-bootstrap/Container';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Skeleton from 'react-loading-skeleton';

// Internal imports
import { CMSContext } from '../App';
import { getAdvisories, getAdvisoriesPreview, markAdvisoriesAsRead } from '../Components/data/advisories';
import { NetworkError, NotFoundError, ServerError } from '../Components/data/helper';
import NetworkErrorPopup from '../Components//map/errors/NetworkError';
import ServerErrorPopup from '../Components//map/errors/ServerError';
import Footer from '../Footer';
import FriendlyTime from '../Components/shared/FriendlyTime';
import renderWagtailBody from '../Components/shared/renderWagtailBody';
import overrides from '../Components/map/overrides';
import { faAttributionToggleLabel } from '../Components/map/attributionControlLabels';
import ShareURLButton from '../Components/shared/ShareURLButton';
import { BASE_MAP, MAP_STYLE } from '../env';
import PollingComponent from "../Components/shared/PollingComponent";
import { getCmsContentSeoDescription, setPageSeoDescription } from '../Components/shared/seo';

// Styling
import './AdvisoryDetailsPage.scss';

// OpenLayers
import { applyStyle } from 'ol-mapbox-style';
import { transform } from 'ol/proj';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import GeoJSON from 'ol/format/GeoJSON';
import { Attribution, defaults } from 'ol/control';
import Map from 'ol/Map';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import View from 'ol/View';


const NOT_FOUND_CONTENT = {
  title: 'Advisory Not Found',
  teaser: 'There is currently no published advisory at this URL',
  body: '',
};


// OpenLayers functions
function getMap(advisoryData) {
  const image = new CircleStyle({
    radius: 5,
    fill: null,
    stroke: new Stroke({color: 'red', width: 1}),
  });

  const styles = {
    'Point': new Style({
      image: image,
    }),
    'LineString': new Style({
      stroke: new Stroke({
        color: 'green',
        width: 1,
      }),
    }),
    'MultiPolygon': new Style({
      stroke: new Stroke({
        color: 'rgb(255, 90, 0)',
        width: 2,
      }),
      fill: new Fill({
        color: 'rgba(255, 217, 105, 0.4)',
      }),
    })
  };

  const styleFunction = function (feature) {
    return styles[feature.getGeometry().getType()];
  };

  const geojsonObject = {
    'type': 'FeatureCollection',
    'crs': {
      'type': 'name',
      'properties': {
        'name': 'EPSG:3857',
      },
    },
    'features': [
      {
        'type': 'Feature',
        'geometry': advisoryData.geometry,
      }
    ]
  };

  const vectorSource = new VectorSource({
    features: new GeoJSON().readFeatures(geojsonObject, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    }),
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: styleFunction,
  });

  const tileSource = new VectorTileSource({
    format: new MVT(),
    url: BASE_MAP,
  });

  const tileLayer = new VectorTileLayer({
    source: tileSource,
  });

  // Apply the basemap style from the arcgis resource
  fetch(MAP_STYLE, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
  }).then(function(response) {
    response.json().then(function(glStyle) {
      // const sourceKeys = Object.keys(glStyle.sources);
      // const autoAttribution =
      //   sourceKeys.length > 0 ? glStyle.sources[sourceKeys[0]].attribution : '';
      // const esriPower =
      //   "Powered by <a href='https://www.esri.com/en-us/home' target='_blank'>Esri | </a>";
      // tileSource.setAttributions([esriPower, autoAttribution]);

      const esriPower =
        "Powered by <a href='https://www.esri.com/en-us/home' target='_blank'>Esri</a> | Tiles: <a href='https://catalogue.data.gov.bc.ca/dataset/78895ec6-c679-4837-a01a-8d65876a3da9' target='_blank'>Ministry of Water, Land and Resource Stewardship</a>";
      tileSource.setAttributions([esriPower]);

      // DBC22-2153
      glStyle.metadata['ol:webfonts'] = '/fonts/{font-family}/{fontweight}{-fontstyle}.css';

      for (const layer of glStyle.layers) {
        overrides.merge(layer, overrides[layer.id] || {});
      }

      applyStyle(tileLayer, glStyle, 'esri');
    });
  });

  const attributionControl = new Attribution({
    className: 'ol-attribution attribution-control',
    label: faAttributionToggleLabel('expand', faCircleInfo),
    collapseLabel: faAttributionToggleLabel('collapse', faXmark),
  });

  const mapViewObj = new View({
    // Centered on Downtown Kelowna
    center: transform([-119.49662112970556, 49.887338062986295], 'EPSG:4326', 'EPSG:3857'),
    zoom: 14,
  });

  return new Map({
    target: 'map',
    layers: [
      tileLayer,
      vectorLayer,
    ],
    view: mapViewObj,
    controls: defaults({
      attribution: false,
    }).extend([attributionControl]),
  });
}

export default function AdvisoryDetailsPage() {
  // Context and router data
  const params = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";

  // Context
  const { cmsContext, setCMSContext } = useContext(CMSContext);
  const cmsContextRef = useRef(cmsContext);

  // Refs
  const mapRef = useRef();
  const advisoryRef = useRef(null);
  const pendingMapUpdate = useRef(false);

  // UseState hooks
  const [activeTab, setActiveTab] = useState('details');
  const [advisory, setAdvisory] = useState(null);
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showUpdateOverlay, setShowUpdateOverlay] = useState(false);
  const initialNotifiedAt = useRef(null);
  const dismissedNotifiedAt = useRef(null);
  const [latestNotifiedAt, setLatestNotifiedAt] = useState(null);
  const currentRevision = useRef(null);

  // Navigating to
  const returnHandler = () => {
    navigate(-1);
  };

  // Error handling
  const displayError = (error) => {
    if (error instanceof ServerError) {
      setShowServerError(true);

    } else if (error instanceof NetworkError) {
      setShowNetworkError(true);
    }
  }

  // Map functions
  const fitMap = (data) => {
    const geom = new GeoJSON().readGeometry(data.geometry, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });

    mapRef.current.getView().fit(geom, {
      padding: [50, 50, 50, 50],
      maxZoom: 12,
      duration: 0
    });
  }

  const updateMapGeometry = (data) => {
  if (!mapRef.current || !data.geometry) return;

  // Find the vector layer (not the tile layer)
  const vectorLayer = mapRef.current.getLayers().getArray().find(
    layer => layer instanceof VectorLayer
  );
  if (!vectorLayer) return;

  const newSource = new VectorSource({
    features: new GeoJSON().readFeatures({
      type: 'FeatureCollection',
      crs: {
        type: 'name',
        properties: { name: 'EPSG:3857' },
      },
      features: [{ type: 'Feature', geometry: data.geometry }]
    }, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    }),
  });

  vectorLayer.setSource(newSource);
};

  const checkForUpdates = async () => {
    try {
      const latestData = await getAdvisories(params.id);

      if (!latestData) return;

      // Detect any publish (major or minor)
      const contentChanged =
        latestData.live_revision !== currentRevision.current;

      if (contentChanged) {
        setAdvisory(latestData);

        currentRevision.current = latestData.live_revision;

        if (latestData.geometry && mapRef.current) {
          updateMapGeometry(latestData);
          // Only fitMap if map tab is visible, otherwise defer
          if (activeTab === 'map') {
            fitMap(latestData);
          } else {
            pendingMapUpdate.current = true;
          }
        }

        document.title = `DriveBC - Advisories - ${latestData.title}`;
        setPageSeoDescription(getCmsContentSeoDescription(latestData));
      }

      // Detect major update only
      const latestNotified = latestData.last_notified_at;

      if (
        latestNotified &&
        latestNotified !== initialNotifiedAt.current &&
        latestNotified !== dismissedNotifiedAt.current
      ) {
        setLatestNotifiedAt(latestNotified);
        initialNotifiedAt.current = latestNotified;
        setShowUpdateOverlay(true);
      }
    } catch (error) {
      // silently ignore
    }
  };

  const loadAdvisory = async () => {
    let advisoryData;

    try {
      advisoryData = await (isPreview ? getAdvisoriesPreview(params.id) : getAdvisories(params.id));
    } catch (error) {
      if (error instanceof NotFoundError) {
        advisoryData = NOT_FOUND_CONTENT;
      } else {
        displayError(error);
      }
    }

    setAdvisory(advisoryData);

    if (advisoryData.id) {
      initialNotifiedAt.current = advisoryData.last_notified_at;
      currentRevision.current = advisoryData.live_revision;

      if (!mapRef.current) {
        mapRef.current = getMap(advisoryData);
      }

      fitMap(advisoryData);

      document.title = `DriveBC - Advisories - ${advisoryData.title}`;
      setPageSeoDescription(getCmsContentSeoDescription(advisoryData));

      markAdvisoriesAsRead([advisoryData], cmsContext, setCMSContext);
    }

    setShowLoader(false);
  };

  useEffect(() => {
    cmsContextRef.current = cmsContext;
  }, [cmsContext]);

  useEffect(() => {
    loadAdvisory();
  }, [params.id]);

  useEffect(() => {
    return () => {
      if (advisory && advisory.id) {
        markAdvisoriesAsRead([advisory], cmsContextRef.current, setCMSContext);
      }
    };
  }, [advisory, setCMSContext]);

  useEffect(() => {
    if (activeTab === 'map') {
      setTimeout(() => {
        fitMap(advisory);
        mapRef.current.updateSize();
      }, 100);
    }
  }, [activeTab]);

  useEffect(() => {
    advisoryRef.current = advisory;
}, [advisory]);

  // Tabs view on mobile
  const advisoryDetails = <FontAwesomeIcon icon={faMemoCircleInfo} />;
  const advisoryMap = <FontAwesomeIcon icon={faMap} />;


  let content = advisory;
  if (content && params.subid) {
    content = advisory.subpages.filter((sub) => sub.slug === params.subid)[0];
  }

  // Rendering
  return (
    <div className='advisory-page cms-page'>
      {showUpdateOverlay && (
        <div className="update-overlay">
          <span className="update-overlay__message">
            Advisory updated just now
          </span>
          <button
            className="update-overlay__close"
            aria-label="Dismiss"
            onClick={() => {
              dismissedNotifiedAt.current = latestNotifiedAt;
              setShowUpdateOverlay(false);
            }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <PollingComponent runnable={checkForUpdates} interval={5000} />
      {showNetworkError &&
        <NetworkErrorPopup />
      }

      {!showNetworkError && showServerError &&
        <ServerErrorPopup setShowServerError={setShowServerError} />
      }

        <div className="page-header">
          <Container id="back-container">
            <a
              className="back-link"
              onClick={returnHandler}
              onKeyDown={keyEvent => {
                if (['Enter', 'NumpadEnter'].includes(keyEvent.key)) {
                  returnHandler();
                }
              }}
              tabIndex={0}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to last page
            </a>
          </Container>
        </div>

        <Container className="page-header__title">
          {content && !showLoader &&
            <React.Fragment>
              {isPreview && <span className="preview-badge">Preview</span>}
              <h1 className="page-title">{content.title}</h1>
              {content.teaser &&
                <p className="page-description body--large">{content.teaser}</p>
              }

              {(content.first_published_at || !content.live) &&
                <div className="page-header__title__meta">
                  <div className="timestamp-container">
                    <span>
                      {content.live
                        ? content.first_published_at !== content.last_published_at
                          ? "Updated"
                          : "Published"
                        : content.first_published_at !== content.last_published_at
                          ? "Updated"
                          : content !== NOT_FOUND_CONTENT ? "Saved" : ""}
                    </span>
                    {content !== NOT_FOUND_CONTENT && (
                        <FriendlyTime date={content.last_notified_at ?? content.last_published_at} />
                      )}
                  </div>
                  <ShareURLButton />
                </div>
                }
            </React.Fragment>
          }

          {showLoader &&
            <div>
              <br/><Skeleton width={280} height={36}/>
              <br/><Skeleton width={320} height={24}/>
              <br/><Skeleton width={240} height={18}/>
            </div>
          }
        </Container>

      <Tabs
        id="advisory-details"
        activeKey={activeTab}
        onSelect={(selectedTab) => setActiveTab(selectedTab) }>

        <Tab eventKey="details" title={<span>{advisoryDetails}Details</span>}>
          <Container className="advisory-body-container cms-body">
            {showLoader &&
              <Skeleton width={320} height={40} />
            }

            {content && !showLoader &&
              <div>{renderWagtailBody(content.body)}</div>
            }
          </Container>
        </Tab>

        <Tab eventKey="map" className={params.subid ? 'hide': ''} title={<span>{advisoryMap}Map View</span>}>
          <Container className="advisory-map-container">
            {showLoader && <Skeleton height={300}/>}

            <div id="map" className={'advisory-map' + (showLoader ? ' hidden' : '')}></div>
          </Container>
        </Tab>
      </Tabs>

      {(activeTab === 'details') &&
        <Footer />
      }
    </div>
  );
}
